import { Download, FileText, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';

import { API_URL } from '../../config/api';
interface Document { id: number; doc_type: string; title: string; content: string; created_at: string; agent_name?: string }
interface ProjectDocumentsProps { projectId: number; isResearch?: boolean }

export default function ProjectDocuments({ projectId, isResearch }: ProjectDocumentsProps) {
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadDocuments = async () => {
      try {
        const res = await fetch(isResearch ? `${API_URL}/api/research/projects/${projectId}/documents` : `${API_URL}/api/projects/${projectId}/documents`)
        const data = await res.json()
        const docs = data.documents as Document[]
        setDocuments(docs || [])
      } catch (err) {
        console.error('Documents load error:', err)
      } finally {
        setLoading(false)
      }
    }
    void loadDocuments()
  }, [projectId])

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: 0 }}>
          <FileText size={24} /> Documents ({documents.length})
        </h2>
        <button style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Upload size={16} /> Upload
        </button>
      </div>
      {documents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-tertiary)' }}>
          <FileText size={48} style={{ opacity: 0.5, marginBottom: '12px' }} />
          <h3 style={{ margin: '0 0 8px' }}>No documents yet</h3>
          <p style={{ fontSize: 13, marginBottom: 16 }}>Export research findings as a document</p>
          <button
            onClick={() => {
              // Export findings as markdown
              fetch(isResearch ? `${API_URL}/api/research/projects/${projectId}/findings?limit=100` : `${API_URL}/api/projects/${projectId}/findings?limit=100`)
                .then(r => r.json())
                .then((data: any) => {
                  const findings = data.findings || []
                  let md = `# Research Findings Export\n\nProject ID: ${projectId}\nExported: ${new Date().toISOString()}\nTotal: ${findings.length} findings\n\n---\n\n`
                  findings.forEach((f: any, i: number) => {
                    md += `## Finding #${f.id || i + 1}\n`
                    md += `**Agent:** ${f.agent_name || 'Unknown'}\n`
                    md += `**EQ Score:** ${f.eq_score || 'N/A'}\n`
                    md += `**Verified:** ${f.verified ? 'Yes' : 'No'}\n`
                    md += `**Date:** ${f.created_at ? new Date(f.created_at).toLocaleDateString() : 'N/A'}\n\n`
                    md += `${f.content || 'No content'}\n\n---\n\n`
                  })
                  const blob = new Blob([md], { type: 'text/markdown' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `research-findings-${projectId}.md`
                  a.click()
                  URL.revokeObjectURL(url)
                })
                .catch(() => {})
            }}
            style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#667eea', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, margin: '0 auto' }}
          >
            <Download size={14} /> Export Findings as Markdown
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {documents.map(doc => (
            <div key={doc.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', transition: 'all 0.2s', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FileText size={20} style={{ color: '#667eea' }} />
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>{doc.title}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{doc.doc_type} • {new Date(doc.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <button style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Download size={14} /> Download
                </button>
              </div>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                {(doc.content || "").substring(0, 200)}...
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
